import { useMutation } from '@tanstack/react-query';
import { fabric } from 'fabric';
import { Canvas } from 'fabric/fabric-impl';
import { HTTPError } from 'ky';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWizard } from 'react-use-wizard';

import { Camera } from '@/components/common/camera';
import { Button, ButtonVariant } from '@/components/form/button';
import { useSettings } from '@/features/user-settings/hooks/use-settings';
import { calibrate } from '@/features/user-settings/services/calibration';
import { useSize } from '@/hooks';

const getRealDimensions = ({
  width,
  height,
  scalingFactor,
}: {
  width: number;
  height: number;
  scalingFactor: number;
}) => {
  return {
    width: parseInt((width / scalingFactor).toFixed(0)),
    height: parseInt((height / scalingFactor).toFixed(0)),
  };
};

export const CalibrationStep = ({ onSave }: { onSave: () => void }) => {
  const { t } = useTranslation();
  const { previousStep } = useWizard();
  const { settings, setSettings } = useSettings();
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [canvasImage, setCanvasImage] = useState<string | null>(null);
  const [rectSize, setRectSize] = useState<{
    width: number | undefined;
    height: number | undefined;
  }>({ width: undefined, height: undefined });
  const scalingFactorRef = useRef(1);
  const rectScalingFactorRef = useRef(2);
  const [isReadyToSave, setIsReadyToSave] = useState(false);

  // a hook to determine the size of canvas container
  const canvasContainerRef = useRef(null);
  const canvasContainerSize = useSize(canvasContainerRef);

  // initialize the canvas
  const canvasRef = useRef(null);
  const fabricRef = useRef<Canvas | null>(null);

  // initialize the canvas with cells from task's detections
  useEffect(() => {
    const initFabric = () => {
      if (!canvasContainerSize?.width) return;
      fabricRef.current = new fabric.Canvas(canvasRef.current, {
        width: canvasContainerSize?.width,
        height: canvasContainerSize?.width,
        preserveObjectStacking: true,
        selection: false,
      });
      fabricRef.current?.on('object:scaling', (e) => {
        if (!e.target) return;
        updateRectSize(e.target.getScaledWidth(), e.target.getScaledHeight());
      });
    };
    const disposeFabric = () => {
      fabricRef.current && fabricRef.current.dispose();
    };
    initFabric();
    return () => {
      disposeFabric();
    };
  }, [canvasContainerSize?.x]);

  // since we hide the default capture button in camera view,
  // we have to pass a ref to trigger the capture via independent click event
  const captureBtn = useRef<HTMLButtonElement | null>(null);

  const [generalError, setGeneralError] = useState('');
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [cameraImage, setCameraImage] = useState<string | null>(null);

  const { isLoading, mutate, isSuccess, data } = useMutation({
    mutationFn: (imageString: string) => calibrate(imageString),
    onSuccess: (data: any) => {
      const imageString = data.imageString;
      // if success, set canvas's image to calibration API image
      imageString && setCanvasImage(data?.imageString);
      setIsReadyToSave(true);
    },
    onError: async (_error, imageString: string) => {
      const error = _error as HTTPError;
      if (!error.response.ok) {
        const errorMessage = await error.response.json<{ message: string }>();
        setGeneralError(errorMessage.message);
      }
      // if failed, set canvas's image to camera image
      setCanvasImage(imageString);
    },
    onSettled: () => {
      setIsFetched(true);
      // in any case, we set the canvas ready
      setIsCanvasReady(true);
    },
  });

  const resetCanvas = () => {
    fabricRef.current?.getObjects().forEach((obj) => {
      fabricRef.current?.remove(obj);
    });
    const image = new fabric.Image('');
    fabricRef.current?.setBackgroundImage(image, () => {});
    fabricRef.current?.setBackgroundColor('transparent', () => {});
    fabricRef.current?.requestRenderAll();
  };

  const reset = () => {
    resetCanvas();
    setRectSize({ width: undefined, height: undefined });
    setCanvasImage(null);
    setCameraImage(null);
    setIsFetched(false);
    setIsCanvasReady(false);
    setGeneralError('');
  };

  const handleOnCapture = (data: string | null) => {
    reset();
    setCameraImage(data);
    data && mutate(data);
  };

  const handleSave = () => {
    const detectedSquareSize = rectSize.height
      ? +rectSize.height
      : ((data as any)?.squareSize as number);
    if (detectedSquareSize) {
      setSettings({ ...settings, detectedSquareSize });
      onSave();
    }
  };

  const updateRectSize = (width: number, height: number) => {
    const dimensions = getRealDimensions({
      width: width * rectScalingFactorRef.current,
      height: height * rectScalingFactorRef.current,
      scalingFactor: scalingFactorRef.current,
    });
    setRectSize({ width: dimensions.width, height: dimensions.height });
  };

  // once canvas mounted we place cells on it
  useEffect(() => {
    if (!canvasContainerSize?.width || !canvasImage || !fabricRef.current) return;
    for (const obj of fabricRef.current.getObjects()) {
      if (obj.get('type') === 'image') fabricRef.current?.remove(obj);
    }
    const lengthOfCanvasSide = canvasContainerSize.width;
    fabricRef.current?.setWidth(lengthOfCanvasSide);
    fabricRef.current?.setHeight(lengthOfCanvasSide);
    fabric.Image.fromURL(canvasImage, (img) => {
      img.scaleToWidth(lengthOfCanvasSide);
      const centerX = canvasContainerSize.width / 2;
      const centerY = canvasContainerSize.height / 2;
      const left = centerX - img.getScaledWidth() / 2;
      const top = centerY - img.getScaledHeight() / 2;
      img.set({ left, top, selectable: false, hasControls: false, evented: false });
      fabricRef.current?.add(img);
      fabricRef.current?.sendToBack(img);
      setIsCanvasReady(true);
      scalingFactorRef.current = img.getTotalObjectScaling().scaleX;
    });
  }, [canvasImage]);

  const handleDrawManually = () => {
    if (!fabricRef.current) return;
    setGeneralError('');
    fabricRef.current.getObjects().forEach((obj) => {
      if (obj.get('type') === 'rect') fabricRef.current?.remove(obj);
    });
    const rect = new fabric.Rect({
      left: 100,
      top: 100,
      fill: 'transparent',
      width: 50,
      height: 50,
      strokeWidth: 1,
      strokeUniform: true,
      stroke: 'rgba(170, 0, 255, 1)',
      hasControls: true,
      lockScalingFlip: true,
      scaleX: 1,
      scaleY: 1,
      borderScaleFactor: 1,
    });
    rect.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false });
    fabricRef.current.add(rect);
    fabricRef.current.setActiveObject(rect);
    updateRectSize(rect.getScaledWidth(), rect.getScaledHeight());
    rectScalingFactorRef.current = rect.getTotalObjectScaling().scaleX;
    setCanvasImage(cameraImage);
    setIsReadyToSave(true);
  };

  return (
    <div className="flex flex-col gap-4 mt-4 md:mt-16">
      <div className="grid gap-4 md:gap-6 md:grid-cols-2">
        <div
          className="relative h-full rounded-lg bg-gray min-w-96"
          ref={canvasContainerRef}
        >
          <div className={`absolute inset-0 ${!isCanvasReady && 'z-10'}`}>
            {!isCanvasReady && (
              <Camera onCapture={handleOnCapture} captureBtnRef={captureBtn} />
            )}
          </div>
          <canvas
            id="canvas"
            ref={canvasRef}
            width={canvasContainerSize?.width}
            className="absolute inset-0"
          />
          <p className="py-2 text-sm text-right text-gray-400 bg-white">
            {rectSize.height && rectSize.width
              ? `${rectSize.width} x ${rectSize.height}`
              : ''}
          </p>
        </div>
        <div className="flex flex-col justify-between">
          <div className="flex flex-col gap-4">
            <ol className="flex flex-col gap-4 ml-6 text-lg list-decimal">
              <li>{t('calibration.step3.text1')}</li>
              <li>{t('calibration.step3.text2')}</li>
              <li>{t('calibration.step3.text3')}</li>
            </ol>

            <div className="flex gap-2">
              {isFetched ? (
                <>
                  <Button
                    onClick={handleDrawManually}
                    isLoading={isLoading}
                    classnames="w-full md:w-auto"
                  >
                    {t('userSettings.drawManually')}
                  </Button>
                  <Button
                    onClick={reset}
                    isLoading={isLoading}
                    classnames="w-full md:w-auto"
                    variant={ButtonVariant.Secondary}
                  >
                    {t('calibration.step3.retake')}
                  </Button>
                </>
              ) : (
                <Button
                  isLoading={isLoading}
                  classnames="w-full md:w-auto"
                  onClick={() => captureBtn.current?.click()}
                >
                  {t('calibration.step3.button')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        {isFetched && generalError ? (
          <div className="p-4 font-medium capitalize rounded-lg text-red-light bg-wine-light">
            <span>{generalError}</span>
          </div>
        ) : null}

        {isFetched && isSuccess ? (
          <div className="font-medium text-[#38761d] capitalize bg-beer-light p-4 rounded-lg">
            <span>{t('calibration.step3.success')}</span>
          </div>
        ) : null}
      </div>

      <div className="flex justify-between">
        <Button
          disabled={isLoading}
          onClick={previousStep}
          variant={ButtonVariant.Secondary}
        >
          {t('common.back')}
        </Button>
        <Button disabled={!isReadyToSave} onClick={handleSave}>
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
};
